import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import React, {useEffect, useState} from 'react';
import {useDispatch, useIntl, useModel} from 'umi';
import {Button, Collapse, Form, Popconfirm, Space, Typography} from 'antd';
import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import BranchServiceTreeSelect from '@/components/Filters/BranchServiceSelect';
import LeadSourceDropdownFilters from '@/components/Filters/LeadSourceDropdownFilters';
import ToggleCard from '@/components/ToggleCard/ToggleCard';
import ProForm, {ProFormItem, ProFormText, ProFormTextArea} from '@ant-design/pro-form';
import {createBookingPageRequest, deleteBookingPageRequest, editBookingPageRequest} from '@/services/api/bookingPage';
import {processAPIResponseNow} from '@/utils/request';
import styles from './index.less';
import {useSelector} from '@@/exports';
import * as JourneySelectors from '@/selectors/journeySelectors';
import * as BookingPagesSelectors from '@/selectors/bookingPagesSelectors';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import {getBookingPageLink} from '@/components/BookingPage/utils';
import {useDefaultLeadSourceConfiguration} from '@/utils/hooks';
import {usePubSub} from '@/context/PubSubContext';
import {FETCH_ME} from '@/context/PubSubContext/events';

const { Text } = Typography;

const BookingPageDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const { initialState } = useModel('@@initialState');
  const { publish } = usePubSub();
  const drawerState = useSelector(BookingPagesSelectors.selectBookingPageDrawer);

  const visible = !!drawerState?.mode;
  const mode = drawerState?.mode;
  const bookingPage = drawerState?.bookingPage || null;
  const userId = drawerState?.userId || initialState?.currentUser?.User?.alias;
  const intl = useIntl();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const leadSources = useSelector(JourneySelectors.selectJourneys);
  const defaultLeadSourceConfiguration = useDefaultLeadSourceConfiguration();
  const isCreateMode = mode === 'create';

  const showLeadSources = leadSources?.filter(source => source.type === 'journey')?.length > 1;

  // ---- initialize form on open ----
  useEffect(() => {
    if (visible) {
      if (bookingPage) {
        form.setFieldsValue({
          ...pick(bookingPage, [
            'has_all_agents_option',
            'name',
            'description',
          ]),
          restrictServices:
            bookingPage.branch_services &&
            bookingPage.branch_services.length > 0,
          branch_services: (bookingPage.branch_services || []).map(
            (service) => service.id,
          ),
          lead_source_id: bookingPage?.lead_source?.id,
        });
      }
    } else {
      form.resetFields();
    }
  }, [visible, isCreateMode, bookingPage]);

  const handleClose = () => {
    form.resetFields();
    // Refresh list when closing edit mode (changes were applied on-the-fly)
    publish(FETCH_ME);
    if (drawerState?.onRefresh) drawerState.onRefresh();
    dispatch({
      type: 'bookingPages/closeDrawer',
    });
    if (drawerState?.closeParentDrawer) {
      dispatch({
        type: 'user/toggleDetailsDrawer',
        payload: {},
      });
    }
  };

  const handleCreateBookingPage = (payload: any) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    createBookingPageRequest({
      params: { userId },
      data: payload,
    }).then((response) => {
      setLoading(false);
      processAPIResponseNow(response, {
        onSuccess: () => {
          handleClose();
        },
      }, 'data');
    });
  };

  const handleEditBookingPage = (payload: any) => {
    if (!bookingPage?.id) {
      return;
    }

    const hasChanges = Object.keys(payload).some(key => !isEqual(payload[key], bookingPage[key]));
    if (!hasChanges) return;

    editBookingPageRequest({
      params: { bookingLinkId: bookingPage.id },
      data: payload,
    }).then((response) => {
      processAPIResponseNow(response, {
        showSuccessMessage: true,
        successEntity: INTL_ENTITY.BOOKING_PAGE,
        onSuccess: () => {
          // On-the-fly edit: don't close drawer, just silently update
          // Refresh will happen when drawer is closed
        },
      }, 'data');
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (!values.restrictServices)
        values.branch_services = [];

      handleCreateBookingPage(values);
    } catch (e) {
      setLoading(false);
    }
  };

  const handleValuesChange = (changedValues: any, allValues: any) => {
    // Only trigger on-the-fly updates in edit mode
    const changedKey = Object.keys(changedValues)?.[0];
    if (['name', 'description'].includes(changedKey)) return;

    if (changedKey === 'restrictServices') {
      if (!changedValues.restrictServices)
        handleEditBookingPage({
          branch_services: [],
        });
      return;
    }
    return handleEditBookingPage(changedValues);
  };

  const handleDelete = () => {
    if (!bookingPage?.id) return;

    setLoading(true);
    deleteBookingPageRequest({
      params: { bookingLinkId: bookingPage.id },
    }).then((response) => {
      setLoading(false);
      processAPIResponseNow(response, {
        onSuccess: () => {
          handleClose();
        },
      }, 'data');
    });
  };

  const submitOnEnter = useSubmitOnEnter(
    isCreateMode ? handleSubmit : undefined,
    { enabled: !loading },
  );

  const getFooter = () => {
    return (
      <div style={{ textAlign: 'right' }}>
        <Space>
          {!isCreateMode && (
            <Popconfirm
              title={
                <F
                  entity={INTL_ENTITY.BOOKING_PAGE}
                  action={INTL_ACTION.REMOVE_CONFIRMATION}
                />
              }
              onConfirm={handleDelete}
              placement="topRight"
            >
              <Button
                loading={loading}
                size={'large'}
                danger
              >
                <F id={'common.actions.remove'} defaultMessage={'Remove'} />
              </Button>
            </Popconfirm>
          )}

          <Button
            className={'button-secondary button-bordered'}
            size={'large'}
            type={'primary'}
            onClick={handleClose}
          >
            <F id={'common.actions.close'} defaultMessage={'Close'} />
          </Button>

          {isCreateMode && (
            <Button
              loading={loading}
              size={'large'}
              onClick={handleSubmit}
              type={'primary'}
            >
              <F id={'common.actions.create'} defaultMessage={'Create'} />
            </Button>
          )}
        </Space>
      </div>
    );
  };

  return (
    <Drawer
      title={
        isCreateMode ? (
          <F entity={INTL_ENTITY.BOOKING_PAGE} action={INTL_ACTION.CREATE} />
        ) : (
          <F entity={INTL_ENTITY.BOOKING_PAGE} action={INTL_ACTION.EDIT} />
        )
      }
      open={visible}
      onOpenChange={handleClose}
      width={600}
      footer={getFooter()}
      destroyOnHidden
    >
      <ProForm
        {...submitOnEnter}
        form={form}
        submitter={false}
        onValuesChange={handleValuesChange}
      >
        {!isCreateMode &&
          <ProFormText readonly
                       formItemProps={{
                         style: { margin: 0 },
                       }}
                       render={() =>
                         <a target={'_blank'} href={getBookingPageLink(bookingPage, defaultLeadSourceConfiguration)}>
                           <Typography.Paragraph ellipsis={{ rows: 1 }} code
                                                 copyable={getBookingPageLink(bookingPage, defaultLeadSourceConfiguration)}>{getBookingPageLink(bookingPage, defaultLeadSourceConfiguration)}</Typography.Paragraph>
                         </a>}
          />}

        {/* Name */}
        <ProFormText
          name="name"
          label={intl.formatMessage({ id: 'common.attribute.name' })}
          placeholder={intl.formatMessage({
            id: 'common.attribute.name',
          })}
          fieldProps={{
            onBlur: (e) =>
              handleEditBookingPage({
                [e.target.id]: e.target.value,
              }),
          }}
          rules={[{ required: true }]}
        />

        {/* Description */}
        <ProFormTextArea
          name="description"
          label={intl.formatMessage({ id: 'common.attribute.description' })}
          placeholder={intl.formatMessage({
            id: 'common.attribute.description',
          })}
          fieldProps={{
            onBlur: (e) =>
              handleEditBookingPage({
                [e.target.id]: e.target.value,
              }),
          }}
        />
        {/* Restrict services */}
        <ProFormItem
          style={{ margin: 0 }}
          name="restrictServices"
          valuePropName="checked"
        >
          <ToggleCard
            title={<F id="pages.profile.bookingPages.restrictServices" />}
            description={
              <F id="pages.profile.bookingPages.restrictServicesDescription" />
            }
          />
        </ProFormItem>

        {/* Services */}
        <ProFormItem
          className={styles.bookingPageFormServices}
          label={null}
          style={{ margin: 0 }}
          shouldUpdate={(p, c) => p.restrictServices !== c.restrictServices}
        >
          {({ getFieldValue }) => (
            <BranchServiceTreeSelect
              label={null}
              inScope
              agentId={userId}
              withStaff={false}
              filterMode="service"
              filterKey="service"
              name={'branch_services'}
              style={{
                display: getFieldValue('restrictServices') ? 'block' : 'none',
              }}
            />
          )}
        </ProFormItem>

        {/* Show all employees */}
        <ProForm.Item
          style={{ margin: 0 }}
          name="has_all_agents_option"
          valuePropName="checked"
        >
          <ToggleCard
            title={<F id="pages.profile.bookingPages.showAllEmployees" />}
            description={
              <F id="pages.profile.bookingPages.showAllEmployeesDescription" />
            }
          />
        </ProForm.Item>
        <Collapse
          style={{
            display: showLeadSources ? 'block' : 'none',
          }}
        >
          <Collapse.Panel header="Lead Source" key="leadSource">
            <ProForm.Item
              name="lead_source_id"
              label={null}
              style={{ marginTop: 16 }}
            >
              <LeadSourceDropdownFilters
                leadSourceType={'journey'}
                type={'select'}
              />
            </ProForm.Item>
          </Collapse.Panel>
        </Collapse>
      </ProForm>
    </Drawer>
  );
};

export default BookingPageDrawer;
